// path: frontend/src/pages/Disclaimer.tsx
import { useEffect } from "react";
import { AlertCircle, Shield, BookOpen, Scale } from "lucide-react";

export default function Disclaimer() {
    useEffect(() => {
        document.title = "Disclaimer – AutoQuiz AI";
    }, []);

    return (
        <div className="max-w-4xl mx-auto p-6 text-slate-900 dark:text-slate-100">
            {/* Header */}
            <div className="mb-8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 mb-4">
                    <AlertCircle size={24} />
                </div>
                <h1 className="text-3xl font-bold mb-2">Disclaimer</h1>
                <p className="text-slate-600 dark:text-slate-400">
                    Important information about the use of AutoQuiz AI
                </p>
            </div>

            {/* Main Disclaimer */}
            <div className="card p-6 mb-6 bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/30">
                <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    Educational Use Only
                </h2>
                <p className="text-sm leading-relaxed text-amber-900 dark:text-amber-200">
                    AutoQuiz AI is an <strong>educational tool</strong> designed solely for
                    learning, study assistance, and informational purposes. This application
                    and its content are <strong>not affiliated with, endorsed by, sponsored by,
                        or associated with</strong> any medical licensing board, examination
                    authority, or official medical education institution.
                </p>
            </div>

            {/* Detailed Sections */}
            <div className="space-y-6">
                {/* Not Affiliated */}
                <section className="card p-6">
                    <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        No Affiliation with Examination Boards
                    </h2>
                    <div className="text-sm space-y-2 text-slate-700 dark:text-slate-300">
                        <p>
                            AutoQuiz AI is <strong>NOT</strong> affiliated with, endorsed by, or
                            connected to:
                        </p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>USMLE (United States Medical Licensing Examination)</li>
                            <li>NBME (National Board of Medical Examiners)</li>
                            <li>COMLEX (Comprehensive Osteopathic Medical Licensing Examination)</li>
                            <li>MCAT (Medical College Admission Test)</li>
                            <li>AAMC (Association of American Medical Colleges)</li>
                            <li>Any other medical licensing or certification board</li>
                            <li>Any official medical school or educational institution</li>
                        </ul>
                    </div>
                </section>

                {/* Content Accuracy */}
                <section className="card p-6">
                    <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        Content Accuracy and Use
                    </h2>
                    <div className="text-sm space-y-3 text-slate-700 dark:text-slate-300">
                        <p>
                            <strong>No Guarantee of Accuracy:</strong> While we strive to provide
                            accurate educational content, the information generated by AutoQuiz AI
                            is AI-generated and may contain errors, inaccuracies, or outdated
                            information.
                        </p>
                        <p>
                            <strong>Not Official Study Material:</strong> Content provided by
                            AutoQuiz AI should not be considered a substitute for:
                        </p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>Official examination preparation materials</li>
                            <li>Approved textbooks and medical references</li>
                            <li>Licensed medical education programs</li>
                            <li>Professional medical advice or consultation</li>
                        </ul>
                        <p>
                            <strong>Supplementary Tool Only:</strong> Use AutoQuiz AI as a
                            supplementary study aid alongside official resources and proper
                            medical education.
                        </p>
                    </div>
                </section>

                {/* Medical Advice */}
                <section className="card p-6 border-red-200 dark:border-red-900/30 bg-red-50 dark:bg-red-900/10">
                    <h2 className="text-lg font-semibold mb-3 flex items-center gap-2 text-red-700 dark:text-red-400">
                        <AlertCircle className="w-5 h-5" />
                        Not Medical Advice
                    </h2>
                    <p className="text-sm text-red-800 dark:text-red-300">
                        <strong>Important:</strong> AutoQuiz AI does not provide medical advice,
                        diagnosis, or treatment recommendations. The content is for educational
                        purposes only and should never be used as a substitute for professional
                        medical advice, diagnosis, or treatment. Always seek the advice of
                        qualified healthcare providers with questions regarding medical conditions.
                    </p>
                </section>

                {/* Liability */}
                <section className="card p-6">
                    <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <Scale className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        Limitation of Liability
                    </h2>
                    <div className="text-sm space-y-3 text-slate-700 dark:text-slate-300">
                        <p>
                            <strong>Use at Your Own Risk:</strong> By using AutoQuiz AI, you
                            acknowledge and agree that:
                        </p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>
                                You use the service entirely at your own risk
                            </li>
                            <li>
                                We make no warranties regarding exam success or educational outcomes
                            </li>
                            <li>
                                We are not responsible for any examination results or academic performance
                            </li>
                            <li>
                                We are not liable for any damages exceeding the amount paid to us in the past 12 months
                            </li>
                        </ul>
                        <p>
                            <strong>Verify Information:</strong> Always verify any information
                            against official sources, textbooks, and consult with qualified
                            medical educators or professionals.
                        </p>
                    </div>
                </section>

                {/* User Responsibility */}
                <section className="card p-6 bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-900/30">
                    <h2 className="text-lg font-semibold mb-3 text-blue-900 dark:text-blue-200">
                        User Responsibility
                    </h2>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                        Users are solely responsible for verifying the accuracy of all content
                        and for their own exam preparation and educational decisions. AutoQuiz AI
                        is a tool to assist learning but should never be your only source of
                        study material.
                    </p>
                </section>
            </div>

            {/* Contact */}
            <div className="mt-8 p-4 border border-black/10 dark:border-white/10 rounded-lg text-center text-sm text-slate-600 dark:text-slate-400">
                <p>
                    Questions about this disclaimer?{" "}
                    <a
                        href="mailto:services@findtheideas.com"
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                        Contact us
                    </a>
                </p>
                <p className="mt-2 text-xs">
                    Last updated: {new Date().toLocaleDateString()}
                </p>
            </div>
        </div>
    );
}